/**
 * Browse relevance weights, and a pure mirror of the scoring arithmetic.
 *
 * The ranking used to be four mutually-exclusive `function_score` buckets under
 * `score_mode: 'first'`, where the separation between tiers was an arithmetic
 * accident: three buckets returned raw epoch millis (~1.75e12) and the fourth
 * returned epoch/1000 (~1.75e9). Two consequences:
 *
 *   - The organic bucket was gated on `createdDate >= now-30d` while the
 *     external bucket had no age gate at all, so 66,430 organic listings older
 *     than a year fell to the /1000 bucket and ranked three orders of magnitude
 *     BELOW every LotLinx and SportServer listing. 98.5% of active organic
 *     supply is Vendoo, so this was demoting almost all of the inventory the
 *     marketplace most wants to sell.
 *   - `score_mode: 'first'` cannot express "external is worse, but a great
 *     external beats a mediocre organic". A bucket wins outright or not at all.
 *
 * The model here is a merit product, plus a featuring term:
 *
 *     merit = source x freshness x quality x deal x engagement x affinity
 *     score = merit + featuredMax x (1 - featuredAgeDays / featuredWindowDays)
 *
 * Both age terms are straight ramps over a window, floored so they stop mattering
 * rather than going negative. Neither is a half-life, and that is deliberate: a
 * half-life never actually reaches its floor, so a listing was still collecting a
 * freshness boost months after it stopped being new -- 1.37x at 30 days, 1.05x at
 * 90. On a marketplace an item is either recent stock or it is not, and a curve
 * that says "somewhat new forever" is not describing anything real.
 *
 * Featuring is OUTSIDE the product, and that placement is the whole mechanism --
 * not a formatting choice. Grouped into the product instead, as
 * `source x (featured + freshness) x quality x ...`, merit MULTIPLIES the boost:
 * at the live weights a listing featured an hour later with the best possible
 * merit scores 358,700 against 43,132 for one featured now with the worst, an
 * 8.32x merit edge against the 1.00046x edge an hour of featuring is worth. That
 * is the compounding-ratio problem again, so featuring stays a summand.
 *
 * WHY FEATURING ADDS RATHER THAN MULTIPLIES. It multiplied until 2026-09-08, as
 * a decay bounded by `featuredMax`, and that could not order the featured band.
 * The entire range was 346.8 spread over a 30-day half-life, so one day of
 * featuring moved a score by 1.047x while merit swung 4.6x across the live
 * corpus -- featuring recency was two orders of magnitude quieter than merit, so
 * the band came out in merit order with the featuring date as noise. Measured on
 * prod: the 33 listings featured that day landed at ranks 4-203, median 105, and
 * the 2-4d bucket had a BETTER median rank (75) than the <1d bucket (105).
 *
 * Making a multiplicative term dominate takes ratios, and ratios COMPOUND. A
 * per-day step large enough to beat merit, carried across a 90-day window, needs
 * a range upwards of 1e33 -- and Lucene keeps `_score` in a 32-bit float, so past
 * 3.4e38 every score SATURATES to the same value with no error raised anywhere.
 * That was measured on prod too: the top 40 hits came back sharing one identical
 * score and the ranking was simply gone.
 *
 * A sum does not compound. `featuredMax` only has to exceed the merit ceiling
 * times the number of hours in the window -- 43,131 for 90 days at the default
 * uplifts, an ordinary number nowhere near any limit -- and then the featuring
 * time orders the band to the hour while merit orders picks made in the same
 * hour. That is the whole reason the shape changed.
 *
 * Every merit factor floors at 1.0 and is bounded above, so the ordering
 * guarantees below are arithmetic facts rather than hopes. `simulateScore` is the same
 * arithmetic in TypeScript, which is what lets those guarantees be unit-tested
 * without an Elasticsearch cluster -- the painless script is generated from
 * these same numbers.
 *
 * TUNABLE AT RUNTIME. The numbers in this file are the DEFAULTS. The live
 * values come from utils/ranking-config.ts (stored on the cluster, cached ten
 * seconds, edited from /dash/search), and every function below takes a
 * resolved `RankingWeights` so the painless params, the TypeScript mirror and
 * the dash's explain view all read the same object. The exported constants
 * remain for callers and tests that mean "the default model".
 *
 * WHAT IS DELIBERATELY ABSENT: the old shippable boost. 117,461 of 117,484
 * active organic listings are shippable (99.98%), so it was a constant on
 * effectively every document -- a painless branch that bought no ordering.
 */

import { DATA_SOURCE } from '../../data/js/data-sources';
import { DEFAULT_SHUFFLE_JITTER, MAX_SHUFFLE_JITTER, shuffleFactor } from '../seeded-shuffle';

// ============================================================================
// Source tiers
// ============================================================================

export type SourceTier = 'organic' | 'adoption' | 'click-out';

/**
 * Listings we sell on-platform. CJ Dropshipping (40) belongs here despite its
 * high id -- it is imported but fulfilled by us, so the old
 * `dataSourceId >= 27` range was demoting it wrongly.
 */
const ORGANIC_SOURCES: number[] = [DATA_SOURCE.ORGANIC, DATA_SOURCE.CJ_DROPSHIPPING];

/**
 * The same list, exported, for the query layer.
 *
 * `externalMix` at 0 filters results down to these ids rather than weighting
 * the rest to zero, because a zero weight still returns the document.
 */
export const ORGANIC_SOURCE_IDS: readonly number[] = Object.freeze([...ORGANIC_SOURCES]);

/** Pet adoption. Not a commercial click-out, not our own inventory either. */
const ADOPTION_SOURCES: number[] = [DATA_SOURCE.RESCUEGROUPS];

/**
 * Which tier a data source sits in.
 *
 * FAILS CLOSED. An id nobody has classified resolves to `click-out`, the lowest
 * tier -- never to organic. The range it replaces (`dataSourceId >= 27`) was
 * accidentally fail-safe in the same direction, and a lookup table that fell
 * through to full organic weight would have been a silent regression for every
 * feed added after this was written.
 */
export function resolveSourceTier(dataSourceId: number | null | undefined): SourceTier {
  if (dataSourceId === null || dataSourceId === undefined) return 'click-out';
  if (ORGANIC_SOURCES.includes(dataSourceId)) return 'organic';
  if (ADOPTION_SOURCES.includes(dataSourceId)) return 'adoption';
  return 'click-out';
}

// ============================================================================
// Tunables and their resolution
// ============================================================================

/**
 * The knobs. Everything an operator may change at runtime, with `null` on the
 * two derived values meaning "keep the guarantee": tier separation follows the
 * uplift product and the featured ceiling follows the tier separation, so a
 * change to any uplift cannot silently break tier dominance or sink the
 * admin's external picks. An explicit number overrides the derivation -- the
 * dash shows which guarantees that breaks before it is saved.
 */
export interface RankingTunables {
  /** Freshness multiplier for a brand-new listing; decays toward 1.0, never below. */
  freshnessMax: number;
  /**
   * Days over which the freshness boost ramps linearly to nothing. At the window
   * a listing is exactly as fresh as one from three years ago, which is to say
   * not fresh, and it keeps every other factor it had.
   *
   * NOT the old 30-day GATE. The bucket model's hard cut-off dropped everything
   * past 30 days three orders of magnitude, beneath every external feed. This
   * floors at 1.0 instead, which is neutral: past the window a listing ranks on
   * its source, quality, deal and engagement like anything else. Nothing is
   * demoted, a boost simply ends.
   *
   * Measured on prod 2026-09-08, browsable supply by age: 65,557 listings inside
   * 30 days, 14,439 between 30 and 90, 21,041 between 90 days and a year, 66,859
   * over a year old. (The "227 between 30 and 90" this comment used to carry was
   * stale by two orders of magnitude.) So the 30-90 day band is what this change
   * actually moves -- it was still collecting 1.14x to 1.37x under the half-life,
   * and now collects nothing. Everything past a year was already at 1.0000 and is
   * untouched.
   */
  freshnessWindowDays: number;
  /** Quality multiplier ceiling. `quality_pct` (else `quality_score`) is an integer 0-100. */
  qualityMax: number;
  /** Engagement multiplier ceiling. `engagement_score` is 0-1000, written by the cron. */
  engagementMax: number;
  /**
   * Deal multiplier ceiling. `deal_score` is 0-100: how far below the going rate
   * a listing is priced, recomputed at index time from the stored market comp
   * and the CURRENT asking price.
   *
   * Above quality by default and deliberately so. Quality maxes out on almost
   * every organic listing -- 99.98% are shippable, verified sellers upload full
   * photo sets -- so it separates almost nothing, while a discount against the
   * comp separates two otherwise identical listings exactly the way a buyer
   * would. Coverage is partial and will be for months, and that is safe: an
   * unscored listing scores 0, which is a factor of 1.0, which is where it
   * already sat.
   */
  dealMax: number;
  /**
   * Seller multiplier ceiling. `seller_sell_through` is the share of a seller's
   * Vendoo listings that sold anywhere within 14 days, smoothed toward the
   * average (utils/seller-sell-through.ts). The factor reaches its ceiling at
   * SELLER_RATE_CAP; a listing with no rate scores at SELLER_RATE_DEFAULT.
   *
   * It took deal's weight on 2026-10-02 (dealMax 1.6 -> 1.0), so the product
   * ceiling, the tier gaps and the featured step did not move. Measured on the
   * judge trial: seller sell-through AUC 0.707 against sales, deal_score 0.494.
   */
  sellerMax: number;
  /**
   * Affinity multiplier ceiling: the share of the buyer's tag-affinity weight a
   * listing covers. Equal to freshness by default -- matching everything the
   * buyer looked at is worth as much as being brand new, and no more.
   */
  affinityMax: number;
  /**
   * Days over which a pick's featuring boost ramps linearly to nothing.
   *
   * LINEAR, not a half-life, and that is the point. An exponential's steps shrink
   * as it ages, so whatever resolution it orders the band at on day 1 it has lost
   * by day 60; a straight ramp moves the same amount every hour, which is what
   * makes "ordered by featuring time to the hour" hold across the WHOLE window
   * rather than just its first few days.
   *
   * There is no cut-off. The term reaches zero at the end of the window and a
   * pick simply becomes an ordinary listing, ranked on merit like everything
   * else. Nothing steps off a cliff.
   */
  featuredWindowDays: number;
  /**
   * Featured ceiling on a PERSONALISED query. Measured: at the browse ceiling
   * the admin's picks filled every personalised row (0 of 20 matches for a
   * watch buyer's vintage row). Below a full match on a fresh listing, above quality.
   */
  featuredMaxPersonalized: number;
  /**
   * What featuring MULTIPLIES a pick's score by the instant it is picked, fading
   * in a straight line to 1 across the featured window, like freshness.
   *
   * Owner, 2026-10-30: "featured boost should also be switched to a multiplier
   * like the rest." The additive boost (featuredMax) put every pick above every
   * listing nobody picked: even its smallest setting, 0.25x the best merit any
   * listing can reach (~24 points), was 12x a typical listing's whole score
   * (~2). Picks took 46% of a week's slots. A multiplier scales with the
   * listing, so a strong unpicked listing can outrank a weak pick, and rotation
   * and already seen step picks down too.
   */
  featuredLift: number;
  /** Ratio between adjacent source tiers. `null` derives ceil(upliftMax) so every tier strictly dominates the next. */
  tierSeparation: number | null;
  /**
   * RETIRED from scoring 2026-10-30 (featuredLift replaced it); still resolved
   * so saved configs and the ranking history read back.
   *
   * What a pick featured this instant added to its score.
   *
   * `null` derives `ceil(1.2 x upliftMax x featuredWindowDays x 24)`, which is
   * the number that makes the ordering a fact: divided across the window's hours
   * it leaves each hour of featuring worth more than the entire merit product, so
   * a pick featured an hour earlier cannot be overtaken however good the listing
   * that follows it is. Merit still orders picks made inside the same hour.
   */
  featuredMax: number | null;
  /**
   * Per-data-source weight, overriding the source's tier. Keys are data source
   * ids as strings; values are absolute weights in (0, 1]. This is how "push
   * Vendoo" or "demote one feed" is expressed without touching the tiers.
   */
  sourceOverrides: Record<string, number>;
  /**
   * Per-data-source strength of the featuring term, 0-4. Keys are data source
   * ids as strings; an absent source is 1. The featuring term is multiplied by
   * it, so 0.5 makes that source's picks half as strong and 0 makes featuring
   * do nothing for it. The merit weight (`sourceOverrides`) never reaches the
   * featuring term, which is why a feed's featured picks sat level with ours.
   */
  featuredSourceWeights: Record<string, number>;
  /**
   * Per-data-source head start for featured picks, in days. A pick from a source
   * with a 30-day head start scores as if it had been featured 30 days more
   * recently, for as long as it is inside the featured window. A head start of
   * the whole window puts every one of that source's picks above every pick
   * from a source with none: that is how "our featured before LotLinx featured"
   * is said. Absent is 0.
   */
  featuredHeadStartDays: Record<string, number>;
  /**
   * 0-20. On the front page, a visitor who came through a partner widget (?p=,
   * or the partner on their account) gets that partner's featured picks added
   * to their interest vector, worth this many listing views
   * (utils/partner-affinity.ts). 0 is off.
   */
  partnerAffinityViews: number;
  /**
   * 0-24. Most listings one seller may hold in a front-page row of listings
   * related to a partner's picks (utils/partner-related.ts). 0 is none: the
   * rows lead with nothing related. 24 is more than a row holds.
   * Two by default, as in Similar Listings: one seller's colourways have
   * near-identical titles and would otherwise fill the row.
   */
  relatedPerSeller: number;
  /**
   * The same limit per data source id, overriding `relatedPerSeller`. A feed
   * lists every one of its items under one seller account, so a feed's limit is
   * really how many of that feed's listings a row may hold. 0 keeps the source
   * out of related rows.
   */
  relatedPerSellerBySource: Record<string, number>;
  /**
   * How much external inventory appears at all. 0 to 1.
   *
   * A dial rather than a tier edit because the tiers express ORDER -- organic
   * beats adoption beats click-out -- and this expresses PRESENCE. Scales the
   * non-organic tier weights, so 1 is today's behaviour and lower values push
   * feeds down without touching the separation that keeps them below organic.
   *
   * ZERO IS A FILTER, NOT A WEIGHT. A multiplicative weight of 0 makes a listing
   * score 0, which sorts it last but still returns it -- and a result page whose
   * tail is entirely feed listings is not "no externals". At 0 they are excluded
   * from the query, which is what "phase them out" has to mean to be useful.
   */
  externalMix: number;
  /**
   * How much sold inventory appears. 0 to 1.
   *
   * Zero is the historical behaviour and the default: BROWSABLE_STATUSES omits
   * SOLD because showing an item that is gone is a dead end, and 31,811 sold
   * listings were once eligible on the home showcases by accident.
   *
   * Above zero admits them at that weight. A sold listing is evidence of what
   * the marketplace carries even when this particular one is gone -- useful on a
   * thin category page, actively bad on a page with plenty of live stock -- so
   * this is deliberately a dial and deliberately off until someone turns it on.
   */
  soldMix: number;
  /**
   * How much held inventory appears. 0 to 1.
   *
   * HELD is NOT a lesser SOLD, which is what an earlier version of this file
   * assumed when it excluded both together. A hold is a buyer's request with a
   * Stripe authorisation behind it, waiting on the seller -- and it is
   * temporary by construction, because an uncaptured card authorisation
   * survives seven days and then expires. Most holds end with the item back on
   * the market, so hiding it for a week costs a week of discovery on an item
   * that is probably still for sale.
   *
   * Defaults to 0.5: held items appear, ranked below an equivalent live
   * listing. That is the honest ordering -- a buyable item should beat one
   * someone else has a claim on, but a held item is still evidence of what the
   * marketplace carries and is very often about to be buyable again.
   */
  heldMix: number;
  /**
   * How many of a seller's listings in one category group rank at full
   * weight. 0 turns the step-down off. Read with `sellerRepeatFactor`.
   *
   * Every listing carries `seller_rank`, its place among its seller's live
   * listings in its category group, featured first then best first by merit
   * (utils/seller-rank.ts). Ranks
   * 0..N-1 score normally; each further band of N is multiplied by
   * `sellerRepeatFactor` once more. It exists because one seller's bulk upload
   * ranks together -- every new listing gets the same freshness boost -- and on
   * 2026-10-08 a Jellycat batch held 26-32 of 32 slots from page 3 of the home
   * grid. Every feed lists under one seller id, so this also keeps a feed from
   * filling a category. The name is kept from when it was a per-page cap so
   * saved configs and experiment arms still read.
   */
  maxPerSellerPerPage: number;
  /**
   * 0.01-1. What each further band of a seller's listings is multiplied by.
   * At 0.25 a seller's 4th-6th listings score a quarter, 7th-9th a sixteenth.
   * 1 is no step-down at all.
   */
  sellerRepeatFactor: number;
  /**
   * 1-8. On the default home feed, one slot in this many goes to an admin pick
   * (utils/home-feed-mix.ts). 1 is every slot until the picks run out; 8 is one
   * a row-and-a-half. Not a score term: the home API reads it to lay out the
   * page, so it has no guarantee to keep.
   */
  homeFeaturedEvery: number;
  /**
   * 0-12. At most this many of one seller in the first dozen slots of the
   * home grid, the home and tag rows, and browse/search (utils/top-seller-cap.ts).
   * A reorder within the page, not a score term, so it has no guarantee to
   * keep. 0 is off.
   */
  maxPerSellerInTop: number;
  /**
   * Retire listings older than this many days, by `createdDate`. 0 is off.
   *
   * A FILTER, like externalMix at 0: freshness already ranks an old listing
   * below a new one, but it still returns it, and a page that runs out of new
   * stock fills its tail with listings years old. On prod 2026-10-09, 67k of
   * 223k browsable organic listings were over 180 days old, 52k of them one
   * seller's paper collectibles from Nov 2024 - Mar 2025. A seller's own page
   * ignores it.
   */
  maxListingAgeDays: number;
  /**
   * 0-0.5. The per-page-load shuffle (utils/seeded-shuffle.ts): every score is
   * multiplied by 1 +- this, by a hash of the listing id and the page load's
   * seed. Close scores trade places on a reload; far-apart ones do not, and
   * one page load keeps one order so paging stays consistent. A request with
   * no seed, and any chosen sort, is untouched. 0 is off.
   */
  shuffleJitter: number;
  /**
   * 0-5. Rotation: a listing shown to many people lately steps down so the
   * next ones get their turn (owner, 2026-10-26: "cycle through many top
   * sellers' high quality items so as many buyers can see them"). The score is
   * divided by 1 + this x reach, where reach is the people the listing was
   * shown to in the last day or two (`exposure_score`, utils/exposure-score.ts)
   * over ROTATION_REACH, capped at 1. Merit still decides who comes back
   * first: a listing twice as good wins its place back at twice the exposure.
   * Scales the featured term too, so admin picks take turns as well. 0 is off.
   */
  rotationStrength: number;
  /**
   * 0.1-1. What a listing this visitor was already shown is worth, as a share
   * of its score, on home (utils/seen-listings.ts): a reload or a return visit
   * leads with what they have not seen. 1 is off.
   */
  seenFactor: number;
  /**
   * Whether keyword search and product-page similar items carry the buyer's
   * affinity vector, and on what terms.
   *
   *   'off'      Search is exactly what it is today. No vector reaches it. The
   *              default, because the front page and search are still ordered by
   *              hand-picked featured listings and that must not move yet.
   *   'additive' The vector applies, but featuring keeps its BROWSE ceiling. Every
   *              factor floors at 1.0, so this can only lift a result, never
   *              demote one, and an admin pick at ~347x still dwarfs a full
   *              affinity match at 2x. Manual featured search is untouched.
   *   'full'     As the showcases: featuring drops to its personalised ceiling
   *              and the buyer's signal can outrank an unrelated admin pick.
   *
   * The deal factor is not gated here. It floors at 1.0 like everything else and
   * peaks at 1.6x against a featured ceiling two orders of magnitude above it,
   * so it is additive on every surface by construction.
   */
  searchPersonalization: SearchPersonalization;
}

/** See `RankingTunables.searchPersonalization`. */
export type SearchPersonalization = 'off' | 'additive' | 'full';

export const DEFAULT_TUNABLES: Readonly<RankingTunables> = Object.freeze({
  freshnessMax: 2.0,
  freshnessWindowDays: 30,
  qualityMax: 1.3,
  engagementMax: 2.0,
  dealMax: 1.0,
  sellerMax: 1.6,
  affinityMax: 2.0,
  featuredWindowDays: 90,
  featuredMaxPersonalized: 1.5,
  featuredLift: 3,
  tierSeparation: null,
  featuredMax: null,
  sourceOverrides: {},
  featuredSourceWeights: {},
  featuredHeadStartDays: {},
  partnerAffinityViews: 3,
  relatedPerSeller: 2,
  relatedPerSellerBySource: {},
  externalMix: 1,
  soldMix: 0,
  heldMix: 0.5,
  maxPerSellerPerPage: 3,
  sellerRepeatFactor: 0.25,
  homeFeaturedEvery: 4,
  maxPerSellerInTop: 0,
  maxListingAgeDays: 0,
  shuffleJitter: DEFAULT_SHUFFLE_JITTER,
  rotationStrength: 2,
  seenFactor: 0.35,
  searchPersonalization: 'off',
});

/** Everything the script and the mirror need, with the derived values filled in. */
export interface RankingWeights {
  freshnessMax: number;
  freshnessWindowDays: number;
  qualityMax: number;
  engagementMax: number;
  dealMax: number;
  sellerMax: number;
  affinityMax: number;
  /** Largest product the non-source factors can reach together. */
  upliftMax: number;
  tierSeparation: number;
  featuredMax: number;
  featuredWindowDays: number;
  /** What one hour of featuring is worth. Above `upliftMax` is the ordering guarantee. */
  featuredHourStep: number;
  featuredMaxPersonalized: number;
  /** A fresh pick's multiplier on browse and search; fades to 1 over the window. */
  featuredLift: number;
  sourceTierWeight: Record<SourceTier, number>;
  sourceOverrides: Record<string, number>;
  /** Featuring-term multiplier per data source id; absent is 1. */
  featuredSourceWeights: Record<string, number>;
  /** Featuring head start per data source id, in days; absent is 0. */
  featuredHeadStartDays: Record<string, number>;
  /** Front page: a partner's picks as an interest prior, in listing views; 0 is off. */
  partnerAffinityViews: number;
  /** Partner-related rows: most listings per seller; 0 is none. */
  relatedPerSeller: number;
  /** The same, per data source id. */
  relatedPerSellerBySource: Record<string, number>;
  /** 0-1. Scales the non-organic tiers; 0 means externals are filtered out entirely. */
  externalMix: number;
  /** 0-1. Weight for SOLD listings; 0 means they are filtered out, which is the default. */
  soldMix: number;
  /** 0-1. Weight for HELD listings. A hold is temporary -- see the tunable. */
  heldMix: number;
  /** A seller's listings per category group at full weight; 0 is off. */
  maxPerSellerPerPage: number;
  /** Multiplier per further band of that many. */
  sellerRepeatFactor: number;
  /** Home feed: one slot in this many is an admin pick, 1-8. */
  homeFeaturedEvery: number;
  /** At most this many of one seller in the first dozen slots; 0 is off. */
  maxPerSellerInTop: number;
  /** Listings created more than this many days ago are filtered out; 0 is off. */
  maxListingAgeDays: number;
  /** The per-page-load score wobble, 0-0.5; 0 is off. */
  shuffleJitter: number;
  /** Rotation by recent reach, 0-5; 0 is off. */
  rotationStrength: number;
  /** A listing this visitor was already shown keeps this share of its score; 1 is off. */
  seenFactor: number;

  searchPersonalization: SearchPersonalization;
  /** Which derived values were overridden by hand, for the dash to flag. */
  derived: { tierSeparation: boolean; featuredMax: boolean };
}

const positive = (n: unknown, fallback: number): number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : fallback;

/** A 0-1 dial. Anything unparseable falls back rather than silently becoming 0. */
const mix = (n: unknown, fallback: number): number =>
  typeof n === 'number' && Number.isFinite(n) && n >= 0 ? Math.min(1, n) : fallback;

/** `homeFeaturedEvery` bounds: every slot a pick, down to one in twenty (owner, 2026-10-30, wanted one in ten; one in eight was the floor). */
/** People reached in the exposure window at which rotation is at full strength. */
export const ROTATION_REACH = 100;
export const MAX_ROTATION_STRENGTH = 5;
export const MIN_SEEN_FACTOR = 0.1;
export const MIN_HOME_FEATURED_EVERY = 1;
export const MAX_HOME_FEATURED_EVERY = 20;

/** `maxPerSellerInTop` ceiling: the whole first dozen. */
export const MAX_PER_SELLER_IN_TOP = 12;

/** The most per seller a related row can be set to hold: more than a row has room for. */
export const MAX_RELATED_PER_SELLER = 24;

/** The strongest partner prior the store accepts, in listing views. */
export const MAX_PARTNER_AFFINITY_VIEWS = 20;

/** The strongest per-source featuring multiplier the store accepts. */
export const MAX_FEATURED_SOURCE_WEIGHT = 4;
/** Largest featuredLift: a fresh pick at most ten times its own merit. */
export const MAX_FEATURED_LIFT = 10;
/** The longest per-source featuring head start the store accepts, in days. */
export const MAX_FEATURED_HEAD_START_DAYS = 365;

/** Keep the numeric-id entries of a per-source map whose value is inside [min, max]. */
function sourceMap(raw: Record<string, number>, min: number, max: number): Record<string, number> {
  const out: Record<string, number> = {};
  // Always an object: resolveRankingWeights copies each map into `t` first.
  for (const [id, value] of Object.entries(raw)) {
    const n = Number(value);
    if (/^\d+$/.test(id) && Number.isFinite(n) && n >= min && n <= max) out[id] = n;
  }
  return out;
}

/** The longest retirement age the store accepts: ten years, past the oldest listing. */
export const MAX_LISTING_AGE_DAYS_CEILING = 3650;

/** A whole count of slots, 0 to 32. Anything unparseable falls back rather than silently becoming 0. */
const perPage = (n: unknown, fallback: number): number =>
  typeof n === 'number' && Number.isFinite(n) && n >= 0 ? Math.min(32, Math.floor(n)) : fallback;

/** Fill in the derived values. Pure; the dash calls it on unsaved edits too. */
export function resolveRankingWeights(tunables: Partial<RankingTunables> = {}): RankingWeights {
  const t: RankingTunables = {
    ...DEFAULT_TUNABLES,
    ...tunables,
    sourceOverrides: { ...(tunables.sourceOverrides || {}) },
    featuredSourceWeights: { ...(tunables.featuredSourceWeights || {}) },
    featuredHeadStartDays: { ...(tunables.featuredHeadStartDays || {}) },
    relatedPerSellerBySource: { ...(tunables.relatedPerSellerBySource || {}) },
  };
  const freshnessMax = Math.max(1, positive(t.freshnessMax, DEFAULT_TUNABLES.freshnessMax));
  const qualityMax = Math.max(1, positive(t.qualityMax, DEFAULT_TUNABLES.qualityMax));
  const engagementMax = Math.max(1, positive(t.engagementMax, DEFAULT_TUNABLES.engagementMax));
  const dealMax = Math.max(1, positive(t.dealMax, DEFAULT_TUNABLES.dealMax));
  const sellerMax = Math.max(1, positive(t.sellerMax, DEFAULT_TUNABLES.sellerMax));
  const affinityMax = Math.max(1, positive(t.affinityMax, DEFAULT_TUNABLES.affinityMax));
  const upliftMax = freshnessMax * qualityMax * engagementMax * dealMax * sellerMax * affinityMax;

  const tierDerived = t.tierSeparation === null || t.tierSeparation === undefined;
  const tierSeparation = tierDerived ? Math.ceil(upliftMax) : Math.max(1, positive(t.tierSeparation, Math.ceil(upliftMax)));

  const featuredWindowDays = positive(t.featuredWindowDays, DEFAULT_TUNABLES.featuredWindowDays);

  // The boost a pick gets the instant it is featured. Derived from the window
  // rather than from the tiers, because what has to be guaranteed is now about
  // TIME: spread across the window's hours, one hour of featuring must outweigh
  // the whole merit product, or merit reorders the band and the featuring date
  // stops meaning anything. The 1.2 is headroom so the comparison is strict.
  const featuredFromWindow = Math.ceil(1.2 * upliftMax * featuredWindowDays * 24);
  const featuredDerived = t.featuredMax === null || t.featuredMax === undefined;
  const featuredMax = featuredDerived
    ? featuredFromWindow
    : Math.max(1, positive(t.featuredMax, featuredFromWindow));

  const sourceOverrides: Record<string, number> = {};
  for (const [id, weight] of Object.entries(t.sourceOverrides)) {
    const w = Number(weight);
    if (/^\d+$/.test(id) && Number.isFinite(w) && w > 0 && w <= 1) sourceOverrides[id] = w;
  }

  const externalMix = mix(t.externalMix, DEFAULT_TUNABLES.externalMix);
  const soldMix = mix(t.soldMix, DEFAULT_TUNABLES.soldMix);
  const heldMix = mix(t.heldMix, DEFAULT_TUNABLES.heldMix);
  const maxPerSellerPerPage = perPage(t.maxPerSellerPerPage, DEFAULT_TUNABLES.maxPerSellerPerPage);
  const sellerRepeatFactor = Math.max(0.01, mix(t.sellerRepeatFactor, DEFAULT_TUNABLES.sellerRepeatFactor));
  const maxListingAgeDays =
    typeof t.maxListingAgeDays === 'number' && Number.isFinite(t.maxListingAgeDays) && t.maxListingAgeDays >= 0
      ? Math.min(MAX_LISTING_AGE_DAYS_CEILING, Math.floor(t.maxListingAgeDays))
      : DEFAULT_TUNABLES.maxListingAgeDays;

  return {
    freshnessMax,
    freshnessWindowDays: positive(t.freshnessWindowDays, DEFAULT_TUNABLES.freshnessWindowDays),
    qualityMax,
    engagementMax,
    dealMax,
    sellerMax,
    affinityMax,
    upliftMax,
    tierSeparation,
    featuredMax,
    featuredWindowDays,
    featuredHourStep: featuredMax / (featuredWindowDays * 24),
    featuredMaxPersonalized: Math.max(1, positive(t.featuredMaxPersonalized, DEFAULT_TUNABLES.featuredMaxPersonalized)),
    featuredLift: Math.min(MAX_FEATURED_LIFT, Math.max(1, positive(t.featuredLift, DEFAULT_TUNABLES.featuredLift))),
    // externalMix scales the non-organic tiers. Organic is always 1, so the
    // dial changes how far BELOW organic the feeds sit without touching the
    // separation that guarantees the order.
    sourceTierWeight: {
      organic: 1,
      adoption: externalMix / tierSeparation,
      'click-out': externalMix / (tierSeparation * tierSeparation),
    },
    externalMix,
    soldMix,
    heldMix,
    maxPerSellerPerPage,
    sellerRepeatFactor,
    homeFeaturedEvery:
      typeof t.homeFeaturedEvery === 'number' && Number.isFinite(t.homeFeaturedEvery)
        ? Math.min(MAX_HOME_FEATURED_EVERY, Math.max(MIN_HOME_FEATURED_EVERY, Math.round(t.homeFeaturedEvery)))
        : DEFAULT_TUNABLES.homeFeaturedEvery,
    maxPerSellerInTop:
      typeof t.maxPerSellerInTop === 'number' && Number.isFinite(t.maxPerSellerInTop) && t.maxPerSellerInTop >= 0
        ? Math.min(MAX_PER_SELLER_IN_TOP, Math.floor(t.maxPerSellerInTop))
        : DEFAULT_TUNABLES.maxPerSellerInTop,
    maxListingAgeDays,
    shuffleJitter:
      typeof t.shuffleJitter === 'number' && Number.isFinite(t.shuffleJitter) && t.shuffleJitter >= 0
        ? Math.min(MAX_SHUFFLE_JITTER, t.shuffleJitter)
        : DEFAULT_TUNABLES.shuffleJitter,
    rotationStrength:
      typeof t.rotationStrength === 'number' && Number.isFinite(t.rotationStrength) && t.rotationStrength >= 0
        ? Math.min(MAX_ROTATION_STRENGTH, t.rotationStrength)
        : DEFAULT_TUNABLES.rotationStrength,
    seenFactor:
      typeof t.seenFactor === 'number' && Number.isFinite(t.seenFactor)
        ? Math.min(1, Math.max(MIN_SEEN_FACTOR, t.seenFactor))
        : DEFAULT_TUNABLES.seenFactor,
    sourceOverrides,
    featuredSourceWeights: sourceMap(t.featuredSourceWeights, 0, MAX_FEATURED_SOURCE_WEIGHT),
    featuredHeadStartDays: sourceMap(t.featuredHeadStartDays, 0, MAX_FEATURED_HEAD_START_DAYS),
    relatedPerSeller:
      typeof t.relatedPerSeller === 'number' && Number.isFinite(t.relatedPerSeller) && t.relatedPerSeller >= 0
        ? Math.min(MAX_RELATED_PER_SELLER, Math.floor(t.relatedPerSeller))
        : DEFAULT_TUNABLES.relatedPerSeller,
    relatedPerSellerBySource: Object.fromEntries(
      Object.entries(sourceMap(t.relatedPerSellerBySource, 0, MAX_RELATED_PER_SELLER)).map(([id, n]) => [id, Math.floor(n)])
    ),
    partnerAffinityViews:
      typeof t.partnerAffinityViews === 'number' && Number.isFinite(t.partnerAffinityViews) && t.partnerAffinityViews >= 0
        ? Math.min(MAX_PARTNER_AFFINITY_VIEWS, t.partnerAffinityViews)
        : DEFAULT_TUNABLES.partnerAffinityViews,
    searchPersonalization:
      t.searchPersonalization === 'additive' || t.searchPersonalization === 'full'
        ? t.searchPersonalization
        : 'off',
    derived: { tierSeparation: tierDerived, featuredMax: featuredDerived },
  };
}

/** The default model, resolved once. */
export const DEFAULT_WEIGHTS: Readonly<RankingWeights> = Object.freeze(resolveRankingWeights(DEFAULT_TUNABLES));

// ============================================================================
// The defaults, as named constants
// ============================================================================
//
// Kept for callers and tests that mean "the default model". Every one of them
// is read from DEFAULT_WEIGHTS; the live values may differ.

export const FRESHNESS_MAX = DEFAULT_WEIGHTS.freshnessMax;
export const FRESHNESS_WINDOW_DAYS = DEFAULT_WEIGHTS.freshnessWindowDays;
export const QUALITY_MAX = DEFAULT_WEIGHTS.qualityMax;
export const ENGAGEMENT_MAX = DEFAULT_WEIGHTS.engagementMax;
export const DEAL_MAX = DEFAULT_WEIGHTS.dealMax;
export const SELLER_MAX = DEFAULT_WEIGHTS.sellerMax;
/** The sell-through at which the seller factor reaches sellerMax. The best sellers ran 18% to 36% on 2026-10-01. */
export const SELLER_RATE_CAP = 0.3;
/**
 * The rate scored for a listing with none: the Vendoo average on 2026-10-01.
 * A seller we know nothing about is ranked as average, not as the worst.
 */
export const SELLER_RATE_DEFAULT = 0.064;
export const AFFINITY_MAX = DEFAULT_WEIGHTS.affinityMax;
export const UPLIFT_MAX = DEFAULT_WEIGHTS.upliftMax;
/**
 * Set above UPLIFT_MAX so each tier STRICTLY dominates the next: an organic
 * listing at its worst still outranks a click-out listing at its best. Enforced
 * by construction (derived) and asserted in the tests.
 */
export const TIER_SEPARATION = DEFAULT_WEIGHTS.tierSeparation;
export const SOURCE_TIER_WEIGHT: Record<SourceTier, number> = DEFAULT_WEIGHTS.sourceTierWeight;
/**
 * What a pick featured this instant adds. Being additive it also sits above
 * UPLIFT_MAX and above the source tiers, so a featured click-out listing still
 * clears the organic band -- 734 of 2,154 active featured listings are external,
 * so featuring has to override the tiers or a third of the admin's picks get
 * demoted. Derived from the window, not a literal: a literal stops delivering the
 * guarantee the moment the window or any uplift changes.
 */
export const FEATURED_MAX = DEFAULT_WEIGHTS.featuredMax;
export const FEATURED_MAX_PERSONALIZED = DEFAULT_WEIGHTS.featuredMaxPersonalized;
export const FEATURED_WINDOW_DAYS = DEFAULT_WEIGHTS.featuredWindowDays;
/** What one hour of featuring is worth; above UPLIFT_MAX is the ordering guarantee. */
export const FEATURED_HOUR_STEP = DEFAULT_WEIGHTS.featuredHourStep;

// ============================================================================
// Source weight
// ============================================================================

/** A source's weight: its explicit override if one is set, else its tier. */
export function sourceWeight(dataSourceId: number | null | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  if (dataSourceId !== null && dataSourceId !== undefined) {
    const override = weights.sourceOverrides[String(dataSourceId)];
    if (override !== undefined) return override;
  }
  return weights.sourceTierWeight[resolveSourceTier(dataSourceId)];
}

/**
 * Painless-friendly `{ [dataSourceId]: weight }` for the ids we classify or
 * override. Click-out ids are absent on purpose: an unmapped id must land on
 * the lowest tier in the script, so a feed added later is demoted, not promoted.
 */
export function sourceWeightMap(weights: RankingWeights = DEFAULT_WEIGHTS): Record<string, number> {
  const map: Record<string, number> = {};
  for (const id of ORGANIC_SOURCES) map[String(id)] = weights.sourceTierWeight['organic'];
  for (const id of ADOPTION_SOURCES) map[String(id)] = weights.sourceTierWeight['adoption'];
  for (const [id, weight] of Object.entries(weights.sourceOverrides)) map[id] = weight;
  return map;
}

/** The per-source maps: an arm or a promotion sets some sources and keeps the rest. */
export const SOURCE_MAP_KEYS = ['sourceOverrides', 'featuredSourceWeights', 'featuredHeadStartDays', 'relatedPerSellerBySource'] as const;

/**
 * Lay a partial set of tunables on a base, merging the per-source maps instead
 * of replacing them. Replaced, an arm that set one source would silently drop
 * every live per-source value it never meant to touch.
 */
export function overlayTunables<T extends Partial<RankingTunables>>(base: T, over: Partial<RankingTunables>): T {
  const out = { ...base, ...over } as T;
  for (const key of SOURCE_MAP_KEYS) {
    (out as Partial<RankingTunables>)[key] = { ...(base[key] ?? {}), ...(over[key] ?? {}) };
  }
  return out;
}

/** How many of one seller a partner-related row may hold for this source; 0 is none. */
export function relatedSellerLimit(dataSourceId: number | null | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  const own = dataSourceId === null || dataSourceId === undefined ? undefined : weights.relatedPerSellerBySource[String(dataSourceId)];
  return own ?? weights.relatedPerSeller;
}

/** How strong featuring is for a source: its entry, else 1. */
export function featuredSourceWeight(dataSourceId: number | null | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  if (dataSourceId === null || dataSourceId === undefined) return 1;
  return weights.featuredSourceWeights[String(dataSourceId)] ?? 1;
}

/** A source's featuring head start in days: its entry, else 0. */
export function featuredHeadStart(dataSourceId: number | null | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  if (dataSourceId === null || dataSourceId === undefined) return 0;
  return weights.featuredHeadStartDays[String(dataSourceId)] ?? 0;
}

// ============================================================================
// Affinity
// ============================================================================

/** One entry of a buyer's tag-affinity vector, as callers supply it. */
export interface AffinityTag {
  tag: string;
  /** Any positive scale; only the share of the total matters. */
  weight: number;
}

/** Lowercased tag -> merged weight. This is what the painless script receives. */
export type AffinityVector = Record<string, number>;

/**
 * Normalise an affinity vector for both the painless params and the TS mirror.
 *
 * Lowercased because `tags` is indexed under a lowercase normaliser, and
 * 1,340 of 2,233 approved tags carry a non-lowercase name. Duplicate tags
 * merge. Non-positive or non-finite weights are dropped rather than clamped: a
 * zero entry would only inflate the denominator.
 */
export function normalizeAffinity(tags: AffinityTag[] | null | undefined): AffinityVector {
  const vector: AffinityVector = {};
  for (const entry of tags || []) {
    if (!entry || typeof entry.tag !== 'string') continue;
    const key = entry.tag.trim().toLowerCase();
    const weight = Number(entry.weight);
    if (!key || !Number.isFinite(weight) || weight <= 0) continue;
    vector[key] = (vector[key] || 0) + weight;
  }
  return vector;
}

/** Sum of a normalised vector's weights; the script's denominator. */
export function affinityTotal(vector: AffinityVector): number {
  let total = 0;
  for (const weight of Object.values(vector)) total += weight;
  return total;
}

/**
 * The listing attribute fields an affinity vector can match, as
 * `<field>:<value>` keys (utils/facet-affinity.ts). The same fields the filter
 * drawer filters on (ATTRIBUTE_FILTER_FIELDS); kept here, not imported, because
 * filter-builders imports this module.
 */
export const AFFINITY_ATTRIBUTE_FIELDS = ['gender', 'age_group', 'size', 'colors', 'brand', 'materials'] as const;

/** A document's attribute values by field, as indexed. */
export type AffinityAttributes = Partial<Record<(typeof AFFINITY_ATTRIBUTE_FIELDS)[number], readonly string[] | string | null>>;

/**
 * Share of the vector's weight a document covers, in [0, 1], through its tags
 * and its attribute fields. Iterates both exactly as the painless loop does --
 * a repeated tag counts twice and the cap at 1.0 absorbs it -- so the two stay
 * identical.
 */
export function affinityShare(
  docTags: readonly string[] | null | undefined,
  vector: AffinityVector | null | undefined,
  docAttributes?: AffinityAttributes | null
): number {
  if (!vector) return 0;
  const total = affinityTotal(vector);
  if (total <= 0) return 0;

  let matched = 0;
  for (const tag of docTags ?? []) {
    const weight = vector[String(tag).toLowerCase()];
    if (weight) matched += weight;
  }
  for (const field of AFFINITY_ATTRIBUTE_FIELDS) {
    const raw = docAttributes?.[field];
    const values = raw == null ? [] : Array.isArray(raw) ? raw : [raw];
    for (const value of values) {
      const weight = vector[`${field}:${String(value).toLowerCase()}`];
      if (weight) matched += weight;
    }
  }
  return Math.min(1, matched / total);
}

/** Affinity uplift. Exactly 1.0 for an empty vector or an unmatched document. */
export function affinityFactor(share: number | null | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  if (!share || share <= 0 || !Number.isFinite(share)) return 1;
  return 1 + (weights.affinityMax - 1) * Math.min(1, share);
}

/** Whether a query is personalised: it carries a vector with any weight at all. */
export function isPersonalized(vector: AffinityVector | null | undefined): boolean {
  return Boolean(vector) && affinityTotal(vector as AffinityVector) > 0;
}

/**
 * The featured lift a query runs under: what a fresh pick's score is multiplied
 * by. Mirrors the script's `featuredLift` param.
 *
 * `preserveBrowseCeiling` is what makes additive search additive: the query
 * carries a vector, so affinity reorders within a tier, but featuring keeps the
 * lift it has on browse.
 */
export function featuredCeiling(
  vector: AffinityVector | null | undefined,
  weights: RankingWeights = DEFAULT_WEIGHTS,
  preserveBrowseCeiling = false
): number {
  if (preserveBrowseCeiling) return weights.featuredLift;
  return isPersonalized(vector) ? weights.featuredMaxPersonalized : weights.featuredLift;
}

// ============================================================================
// Pure mirror of the painless arithmetic
// ============================================================================

export interface ScoreInput {
  /** The document's listingId; with `shuffleSeed`, decides the page load's wobble. */
  listingId?: number | null;
  /** The page load's seed (utils/seeded-shuffle.ts). Absent: no shuffle. */
  shuffleSeed?: number;
  dataSourceId?: number | null;
  /** Milliseconds since epoch. */
  createdDate?: number | null;
  featuredDate?: number | null;
  /** 0-100: quality_pct (utils/quality-percentile.ts), else the raw quality_score. Absent scores neutral. */
  qualityScore?: number | null;
  /** 0-1000, written by the engagement cron. Absent scores neutral. */
  engagementScore?: number | null;
  /** 0-100, written by the indexer from the market comp. Absent scores neutral. */
  dealScore?: number | null;
  /** 0-1, the seller's smoothed sell-through. Absent scores at SELLER_RATE_DEFAULT. */
  sellerSellThrough?: number | null;
  /** The document's own tags, as indexed. */
  tags?: readonly string[] | null;
  /** The document's attribute fields, as indexed; matched as `<field>:<value>`. */
  attributes?: AffinityAttributes | null;
  /** The query's affinity vector. Absent or empty scores neutral. */
  affinity?: AffinityVector | null;
  /** Keep the browse featured ceiling even with a vector. See featuredCeiling. */
  preserveFeaturedCeiling?: boolean;
  /** Place among the seller's live listings in its group, newest first. Absent: not ranked yet. */
  sellerRank?: number | null;
  /** Whether this query applies the per-seller step-down (browse and search do; a seller page does not). */
  sellerCap?: boolean;
  /** Decayed people reached lately, written by the exposure cron. Absent: no rotation. */
  exposureScore?: number | null;
  /** Whether this visitor was already shown the listing (utils/seen-listings.ts). */
  seen?: boolean;
}

/**
 * How much of an age window is left, from 1.0 at zero age to 0.0 at the window
 * and 0.0 for everything past it.
 *
 * The one shape both age terms share. Freshness scales it between 1.0 and its
 * ceiling (a factor must never reach 0 -- under a product that annihilates the
 * listing), featuring multiplies it outright (a summand's neutral value IS 0).
 */
export function ageRamp(
  ageMs: number | null | undefined,
  windowDays: number
): number {
  if (ageMs === null || ageMs === undefined || !Number.isFinite(ageMs)) return 0;
  if (!(windowDays > 0)) return 0;
  const remaining = 1 - Math.max(0, ageMs) / 86_400_000 / windowDays;
  return remaining <= 0 ? 0 : remaining;
}

/**
 * Freshness uplift: the full boost on a listing posted this instant, ramping
 * straight down to exactly 1.0 at the window and staying there. Floors at 1.0,
 * never 0 -- this multiplies, and a factor of 0 would drop the listing out of the
 * ranking entirely however well it matched.
 */
export function freshnessFactor(
  createdDate: number | null | undefined,
  now: number,
  weights: RankingWeights = DEFAULT_WEIGHTS
): number {
  if (!createdDate) return 1;
  return 1 + (weights.freshnessMax - 1) * ageRamp(now - createdDate, weights.freshnessWindowDays);
}

/**
 * How much of a pick's featuring is left: 1 the instant it is picked, falling in
 * a straight line to 0 across the window, times the source's strength, plus its
 * head start in windows. 0 for a listing never featured or past the window.
 * The widget feed orders its site-featured picks by this directly.
 */
export function featuredRamp(
  featuredDate: number | null | undefined,
  now: number,
  windowDays: number = DEFAULT_WEIGHTS.featuredWindowDays,
  /** The source's strength (featuredSourceWeights). */
  strength = 1,
  /** The source's head start in days (featuredHeadStartDays); only while the pick is inside the window. */
  headStartDays = 0
): number {
  if (!featuredDate) return 0;
  const ramp = ageRamp(now - featuredDate, windowDays);
  if (ramp <= 0) return 0;
  return strength * (ramp + headStartDays / windowDays);
}

/**
 * What featuring MULTIPLIES a listing's score by: `lift` the instant it is
 * picked, ramping straight down to 1 across the window, like freshness.
 *
 * 1.0 for a listing that was never featured, and for a pick past the end of the
 * window: the same thing, which is what "the lift has run out" should mean.
 */
export function featuredBoost(
  featuredDate: number | null | undefined,
  now: number,
  lift: number = DEFAULT_WEIGHTS.featuredLift,
  windowDays: number = DEFAULT_WEIGHTS.featuredWindowDays,
  strength = 1,
  headStartDays = 0
): number {
  return 1 + (lift - 1) * featuredRamp(featuredDate, now, windowDays, strength, headStartDays);
}

/**
 * Quality uplift. Floors at 1.0 rather than scaling from zero: a missing or
 * zero `quality_score` must leave a listing where it is, not annihilate it.
 * Under a multiplicative model a factor of 0 removes the document from the
 * ranking entirely no matter how well it matched.
 */
export function qualityFactor(qualityScore: number | null | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  if (!qualityScore || qualityScore <= 0) return 1;
  const normalized = Math.min(100, qualityScore) / 100;
  return 1 + (weights.qualityMax - 1) * normalized;
}

/** Engagement uplift. Exactly 1.0 while the field is unwritten. */
/** Rotation: 1 / (1 + strength x reach), reach = exposure / ROTATION_REACH capped at 1. 1 with no exposure. */
export function rotationFactor(exposureScore: number | null | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  if (!exposureScore || exposureScore <= 0 || !(weights.rotationStrength > 0)) return 1;
  const reach = Math.min(1, exposureScore / ROTATION_REACH);
  return 1 / (1 + weights.rotationStrength * reach);
}

/** What an already-seen listing keeps of its score; 1 when not seen. */
export function seenMultiplier(seen: boolean | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  return seen ? weights.seenFactor : 1;
}

export function engagementFactor(engagementScore: number | null | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  if (!engagementScore || engagementScore <= 0) return 1;
  const normalized = Math.min(1000, engagementScore) / 1000;
  return 1 + (weights.engagementMax - 1) * normalized;
}

/**
 * Deal uplift. Exactly 1.0 for a listing priced at or above the going rate, and
 * for every listing nothing has priced yet -- the absence of an estimate must
 * not be a penalty while coverage is still being built.
 */
export function dealFactor(dealScore: number | null | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  if (!dealScore || dealScore <= 0) return 1;
  const normalized = Math.min(100, dealScore) / 100;
  return 1 + (weights.dealMax - 1) * normalized;
}

/**
 * Seller uplift: 1.0 at a 0% sell-through, sellerMax at SELLER_RATE_CAP and
 * above. A missing rate is scored as the average seller.
 */
export function sellerFactor(rate: number | null | undefined, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  const r = typeof rate === 'number' && Number.isFinite(rate) ? rate : SELLER_RATE_DEFAULT;
  const normalized = Math.min(1, Math.max(0, r) / SELLER_RATE_CAP);
  return 1 + (weights.sellerMax - 1) * normalized;
}

/**
 * Ranks are stored no higher than this, so a big seller's new listing shifts
 * only the first 200 of their documents instead of all of them. Past it the
 * step-down is already negligible: at the defaults, band 66 is 0.25^66.
 */
export const SELLER_RANK_CAP = 200;

/**
 * The per-seller step-down: `sellerRepeatFactor` to the power of the listing's
 * band of `maxPerSellerPerPage`. A listing not ranked yet sits one band down,
 * so a fresh bulk upload cannot top the feed in the minutes before the rank
 * cron reaches it.
 */
export function sellerRepeatMultiplier(
  rank: number | null | undefined,
  applies: boolean,
  weights: RankingWeights = DEFAULT_WEIGHTS
): number {
  const perSeller = weights.maxPerSellerPerPage;
  if (!applies || perSeller <= 0) return 1;
  const r = typeof rank === 'number' && Number.isFinite(rank) && rank >= 0 ? rank : perSeller;
  const band = Math.floor(r / perSeller);
  return band > 0 ? Math.pow(weights.sellerRepeatFactor, band) : 1;
}

export interface ScoreBreakdown {
  /**
   * Added to the merit product, not multiplied into it. Zero when never
   * featured. `score` drops it when the per-seller step-down applies.
   */
  featured: number;
  /** The source's featuring strength the featured term ran under; 1 unless set. */
  featuredSource: number;
  /** The source's featuring head start in days; 0 unless set. */
  featuredHeadStartDays: number;
  /** The product of every factor below: what the listing scores on its own merits. */
  merit: number;
  source: number;
  freshness: number;
  quality: number;
  deal: number;
  seller: number;
  engagement: number;
  affinity: number;
  /** The per-seller step-down on merit; below 1 it also drops the featured boost. 1 when it does not apply. */
  repeat: number;
  /** The page load's wobble on the whole score; 1 with no seed. */
  shuffle: number;
  /** Rotation by recent reach, on the whole score; 1 with no exposure. */
  rotation: number;
  /** The already-seen step-down, on the whole score; 1 when not seen. */
  seen: number;
  /** Share of the affinity vector the document covered, 0-1. */
  affinityShare: number;
  score: number;
}

/** Every factor separately, plus the product. The dash's explain view is this. */
export function scoreBreakdown(input: ScoreInput, now: number, weights: RankingWeights = DEFAULT_WEIGHTS): ScoreBreakdown {
  const share = affinityShare(input.tags, input.affinity, input.attributes);
  const featuredSource = featuredSourceWeight(input.dataSourceId, weights);
  const featuredHeadStartDays = featuredHeadStart(input.dataSourceId, weights);
  const featured = featuredBoost(
    input.featuredDate,
    now,
    featuredCeiling(input.affinity, weights, input.preserveFeaturedCeiling === true),
    weights.featuredWindowDays,
    featuredSource,
    featuredHeadStartDays
  );
  const source = sourceWeight(input.dataSourceId, weights);
  const freshness = freshnessFactor(input.createdDate, now, weights);
  const quality = qualityFactor(input.qualityScore, weights);
  const deal = dealFactor(input.dealScore, weights);
  const seller = sellerFactor(input.sellerSellThrough, weights);
  const engagement = engagementFactor(input.engagementScore, weights);
  const affinity = affinityFactor(share, weights);
  const repeat = sellerRepeatMultiplier(input.sellerRank, input.sellerCap === true, weights);
  const merit = source * freshness * quality * deal * seller * engagement * affinity * repeat;
  const shuffle = shuffleFactor(input.listingId, input.shuffleSeed, weights.shuffleJitter);
  const rotation = rotationFactor(input.exposureScore, weights);
  const seen = seenMultiplier(input.seen, weights);
  return {
    featured,
    featuredSource,
    featuredHeadStartDays,
    merit,
    source,
    freshness,
    quality,
    deal,
    seller,
    engagement,
    affinity,
    repeat,
    shuffle,
    rotation,
    seen,
    affinityShare: share,
    // Past a seller's first band the featured lift is dropped (see
    // buildRelevanceScoreScript). Featuring multiplies like every other factor,
    // so the shuffle, rotation and the seen step-down scale picks too.
    score: merit * (repeat < 1 ? 1 : featured) * shuffle * rotation * seen,
  };
}

/**
 * The complete browse score, in TypeScript.
 *
 * Must stay arithmetically identical to `buildRelevanceScoreScript()`'s painless
 * source. Both read the same weights, and the tests assert the ordering
 * guarantees through this function.
 */
export function simulateScore(input: ScoreInput, now: number, weights: RankingWeights = DEFAULT_WEIGHTS): number {
  return scoreBreakdown(input, now, weights).score;
}
